import {
  PRODUCT_FAMILIES,
  type ProductFamily,
} from '../../shared/productFamily'
import type { ProductImageFraming } from '../../shared/productPresentation'

export type CookieCareFactValue = string | null

export type CookieCareFactEntry = {
  value: CookieCareFactValue
  appliesTo: readonly ProductFamily[]
}

export type CookieCareFact = {
  entries: readonly CookieCareFactEntry[]
}

export type CookieCareProductVisual = {
  src: string
  alt: string
  framing: ProductImageFraming
}

export const COOKIE_CARE_PRODUCT_FAMILIES: readonly ProductFamily[] = PRODUCT_FAMILIES.filter(
  (family) => family !== 'Brownie',
)

const BROWNIE_COOKIE_SANDWICH_FAMILY = ['Brownie Cookie Sandwich'] as const satisfies readonly ProductFamily[]
const MASSIVE_STUFFED_COOKIE_FAMILY = ['Massive Stuffed Cookie'] as const satisfies readonly ProductFamily[]
const BROWNIE_FAMILY = ['Brownie'] as const satisfies readonly ProductFamily[]

function sharedCookieFact(value: string): CookieCareFact {
  return {
    entries: [
      {
        value,
        appliesTo: COOKIE_CARE_PRODUCT_FAMILIES,
      },
    ],
  }
}

export const cookieCareFacts = {
  ownerLockStatus: 'locked' as const,
  lockedAt: '2026-09-29' as string | null,
  storageMethod: sharedCookieFact(
    'Keep sealed in an airtight container or sealed bag at cool room temperature, away from direct sunlight and heat.',
  ),
  roomTemperatureFreshness: sharedCookieFact(
    'Best enjoyed within 2–3 days of receiving them for the best texture.',
  ),
  refrigerationRecommendation: sharedCookieFact(
    'Refrigeration isn’t usually necessary and may firm or dry the cookies. Products containing cream-cheese or cheesecake fillings must be kept refrigerated. Refrigeration is also sensible during unusually hot conditions.',
  ),
  microwaveMethod: {
    entries: [
      {
        appliesTo: BROWNIE_COOKIE_SANDWICH_FAMILY,
        value: 'Best enjoyed at room temperature. If you want the brownie a little softer, remove all packaging and microwave carefully.',
      },
      {
        appliesTo: MASSIVE_STUFFED_COOKIE_FAMILY,
        value: 'For standard fillings, remove all packaging and microwave, then rest before eating. Cheesecake or cream-cheese filled cookies must not be microwaved; enjoy chilled or cool, or allow to soften slightly at room temperature.',
      },
      {
        appliesTo: BROWNIE_FAMILY,
        value: 'Remove all packaging and microwave gently if you prefer it warm. The chocolate topping will soften as it warms.',
      },
    ],
  },
  microwaveTime: {
    entries: [
      {
        appliesTo: BROWNIE_COOKIE_SANDWICH_FAMILY,
        value: 'About 5 seconds. Check before adding more heat; if needed, continue in 2–3 second bursts only because the buttercream can soften or melt very quickly.',
      },
      {
        appliesTo: MASSIVE_STUFFED_COOKIE_FAMILY,
        value: 'Standard fillings: 10–15 seconds, rest for 30 seconds, then add another 5 seconds only if needed. Cheesecake or cream-cheese filled cookies: do not warm.',
      },
      {
        appliesTo: BROWNIE_FAMILY,
        value: '10–15 seconds, let it stand briefly, then add another 5 seconds only if needed.',
      },
    ],
  },
  ovenReheating: {
    entries: [
      {
        appliesTo: BROWNIE_COOKIE_SANDWICH_FAMILY,
        value: 'No oven method recommended.',
      },
      {
        appliesTo: MASSIVE_STUFFED_COOKIE_FAMILY,
        value: 'Standard fillings: 150°C fan for 4–6 minutes, then rest for 1–2 minutes. Cheesecake or cream-cheese filled cookies: do not oven-warm.',
      },
      {
        appliesTo: BROWNIE_FAMILY,
        value: 'No oven method recommended. The buttercream can melt quickly. Use the microwave guidance only if you want it warm.',
      },
    ],
  },
  freezingPackaging: {
    entries: [
      {
        appliesTo: PRODUCT_FAMILIES,
        value: 'Freeze as fresh as possible and completely cooled. Wrap each item individually and tightly in cling film or freezer-safe wrap, then place it inside an airtight freezer bag or sealed freezer-safe container. Remove as much excess air as practical and freeze products individually rather than placing unwrapped items together.',
      },
    ],
  },
  freezerDuration: {
    entries: [
      {
        appliesTo: PRODUCT_FAMILIES,
        value: 'For best quality, use within 1 month of freezing.',
      },
    ],
  },
  thawingMethod: {
    entries: [
      {
        appliesTo: BROWNIE_COOKIE_SANDWICH_FAMILY,
        value: 'Keep wrapped while thawing. Thaw overnight in the fridge, then allow it to come toward room temperature before eating for the best brownie and buttercream texture.',
      },
      {
        appliesTo: MASSIVE_STUFFED_COOKIE_FAMILY,
        value: 'Standard fillings: thaw wrapped in the fridge overnight, then bring closer to room temperature before eating or warming. Cheesecake or cream-cheese filled cookies: thaw in the fridge only while still wrapped, keep refrigerated after thawing, and enjoy chilled or allow to soften slightly before eating.',
      },
      {
        appliesTo: BROWNIE_FAMILY,
        value: 'Keep individually wrapped and thaw overnight in the fridge. Bring to room temperature before eating if preferred.',
      },
    ],
  },
  warmingFromFrozen: {
    entries: [
      {
        appliesTo: BROWNIE_COOKIE_SANDWICH_FAMILY,
        value: 'Do not microwave or oven-warm from frozen. Thaw completely first, then use the normal Brownie Cookie Sandwich warming guidance if desired.',
      },
      {
        appliesTo: MASSIVE_STUFFED_COOKIE_FAMILY,
        value: 'Standard fillings: thawing first is preferred for the most even result. To warm from frozen, remove all wrapping and microwave for 20 seconds, check the centre, then continue in 5–10 second bursts only if needed; rest about 1 minute before eating. Alternatively, warm at 150°C fan for approximately 8–10 minutes, then rest for 1–2 minutes. Cheesecake or cream-cheese filled cookies must not be microwave- or oven-warmed, either frozen or thawed.',
      },
      {
        appliesTo: BROWNIE_FAMILY,
        value: 'Remove all wrapping. Microwave from frozen for 20–30 seconds, checking after 20 seconds and adding another 5–10 seconds only if needed. Do not oven-warm; the buttercream can melt quickly. The chocolate topping will soften as it warms.',
      },
    ],
  },
}

const defaultFraming: ProductImageFraming = {
  focalX: 0.5,
  focalY: 0.5,
  zoom: 1,
}

const summaryFraming: ProductImageFraming = {
  focalX: 0.5,
  focalY: 0.5,
  zoom: 1.3,
}

export const cookieCareContent = {
  summary: {
    eyebrow: 'COOKIE CARE',
    heading: 'KEEP ’EM GOOD.',
    intro: 'Storage, warming and freezing tips for your Sugar Gremlin treats.',
    cues: [
      { label: 'STORE', detail: 'Storage tips' },
      { label: 'WARM', detail: 'Warming tips' },
      { label: 'SAVE', detail: 'Freezing tips' },
    ],
    cta: 'FULL COOKIE CARE',
    image: {
      src: '/assets/homepage-v3/products/sg-menu-product-bueno-sc-master.png',
      alt: 'Sugar Gremlin stuffed cookie',
      framing: summaryFraming,
    } satisfies CookieCareProductVisual,
  },
  hero: {
    eyebrow: 'COOKIE CARE',
    headingLineOne: 'KEEP ’EM GOOD.',
    headingLineTwo: 'COOKIE CARE.',
    intro: 'Storage, warming and freezing guidance for your Sugar Gremlin treats.',
    lockNote: 'Follow the care guidance below for your product type.',
    image: {
      src: '/assets/homepage-v3/products/sg-menu-product-chocolate-bcs-master.png',
      alt: 'Chocolate Brownie Cookie Sandwich',
      framing: defaultFraming,
    } satisfies CookieCareProductVisual,
  },
  quickCards: [
    { id: 'storage', title: 'STORE IT', detail: 'Storage tips', icon: 'store' as const },
    { id: 'warming', title: 'WARM IT', detail: 'Warming tips', icon: 'warm' as const },
    { id: 'freezing', title: 'SAVE IT', detail: 'Freezing tips', icon: 'save' as const },
  ],
  sections: [
    {
      id: 'storage',
      eyebrow: 'STORAGE',
      headingLineOne: 'HOW TO STORE',
      headingLineTwo: 'YOUR COOKIES',
      intro: 'A few simple habits keep your Sugar Gremlin treats tasting their best.',
      imageFirst: true,
      image: {
        src: '/assets/homepage-v3/products/sg-menu-product-vanilla-bcs-master.png',
        alt: 'Vanilla Brownie Cookie Sandwich',
        framing: defaultFraming,
      } satisfies CookieCareProductVisual,
      facts: [
        { label: 'Storage method', fact: cookieCareFacts.storageMethod },
        { label: 'Freshness period', fact: cookieCareFacts.roomTemperatureFreshness },
        { label: 'Refrigeration', fact: cookieCareFacts.refrigerationRecommendation },
      ],
    },
    {
      id: 'warming',
      eyebrow: 'WARMING',
      headingLineOne: 'HOW TO WARM',
      headingLineTwo: 'THEM UP',
      intro: 'A little heat goes a long way. Use the guide below for your treat.',
      imageFirst: false,
      image: {
        src: '/assets/homepage-v3/products/sg-menu-product-og-choc-sc-master.png',
        alt: 'O.G Chocolate stuffed cookie',
        framing: defaultFraming,
      } satisfies CookieCareProductVisual,
      facts: [
        { label: 'Microwave method', fact: cookieCareFacts.microwaveMethod },
        { label: 'Microwave time', fact: cookieCareFacts.microwaveTime },
        { label: 'Oven reheating', fact: cookieCareFacts.ovenReheating },
      ],
    },
    {
      id: 'freezing',
      eyebrow: 'FREEZING',
      headingLineOne: 'HOW TO SAVE',
      headingLineTwo: 'FOR LATER',
      intro: 'Freeze them well, thaw them safely, and warm only where recommended.',
      imageFirst: true,
      image: {
        src: '/assets/homepage-v3/products/sg-menu-product-biscoff-sc-master.png',
        alt: 'Biscoff stuffed cookie',
        framing: defaultFraming,
      } satisfies CookieCareProductVisual,
      facts: [
        { label: 'Freezing packaging', fact: cookieCareFacts.freezingPackaging },
        { label: 'Freezer duration', fact: cookieCareFacts.freezerDuration },
        { label: 'Thawing', fact: cookieCareFacts.thawingMethod },
        { label: 'Warming from frozen', fact: cookieCareFacts.warmingFromFrozen },
      ],
    },
  ],
  faq: {
    eyebrow: 'STILL GOT QUESTIONS?',
    heading: 'Check out our FAQs',
    supportingCopy: 'Find answers about ordering, delivery, allergens and more.',
    cta: 'VIEW FAQs',
    href: '/faq',
  },
} as const
